import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Overline, Metric, Gauge, Empty, Pill } from "@/components/Widgets";
import { PageGuide } from "@/components/PageGuide";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, AreaChart, Area } from "recharts";
import { Detective, PenNib, Image as ImageIcon, CalendarBlank, ArrowRight } from "@phosphor-icons/react";

export default function Overview() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/analytics/overview").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const counts = data?.counts || {};
  const sentiment = data?.sentiment;
  const pie = sentiment
    ? [
        { name: "Positive", value: sentiment.positive, fill: "#E9AC0C" },
        { name: "Neutral", value: sentiment.neutral, fill: "#9D3FFF" },
        { name: "Negative", value: sentiment.negative, fill: "#DC2450" },
      ]
    : [];

  return (
    <div className="space-y-6 fade-up">
      {/* Urban hero banner */}
      <div className="relative overflow-hidden border border-border" data-testid="overview-hero" style={{ minHeight: 200 }}>
        <img src="/overview-hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080808] via-[#080808]/85 to-[#080808]/30" />
        <div className="absolute inset-0 grain" />
        <div className="relative p-8 sm:p-10 max-w-2xl">
          <div className="overline text-primary mb-2">SMI · REPUTATION COMMAND CENTER</div>
          <h1 className="font-head font-bold text-4xl sm:text-5xl leading-[0.95] tracking-tighter">
            Run the <span className="gold-foil">game</span>.<br />Own your name.
          </h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-lg">
            For artists, entrepreneurs, influencers &amp; anyone building a powerful personal brand — scan, rebuild and flood the web with your best story, 24/7.
          </p>
        </div>
      </div>

      <PageGuide title="How to use your dashboard" steps={[
        "Run a Reputation Scan to get your score, then Build a rebuild plan.",
        "Use Content & Image Studio to create positive posts and visuals.",
        "Launch an SEO campaign and click 'Publish to Web & Media' to auto-publish articles.",
        "Share your public profile link — track views and article reach right here.",
      ]} />

      {/* Public profile banner */}
      {data?.public_slug && (
        <div className="relative p-[1px] tracing-border" data-testid="public-profile-banner">
          <div className="bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <Overline className="text-primary">YOUR PUBLIC PROFILE · LIVE ON THE WEB</Overline>
              <div className="text-sm mt-1 font-mono text-muted-foreground truncate">{window.location.origin}/p/{data.public_slug}</div>
              {data.reach && (
                <div className="text-xs text-muted-foreground mt-1">
                  <span className="text-emerald-400 font-mono">{data.reach.profile_views}</span> profile views · <span className="text-emerald-400 font-mono">{data.reach.article_views}</span> article reads
                </div>
              )}
            </div>
            <a href={`/p/${data.public_slug}`} target="_blank" rel="noreferrer" data-testid="view-public-profile"
              className="bg-primary text-primary-foreground font-semibold px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors duration-200 text-center">
              View public profile
            </a>
          </div>
        </div>
      )}

      {/* Top metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Metric label="Reputation Score" value={data?.reputation_score ?? "—"} sub="latest scan" testid="metric-score" />
        <Metric label="Rep. Projected" value={data?.rebuild?.projected ?? "—"} sub={data?.rebuild?.target ? `target ${data.rebuild.target}` : "no rebuild yet"} testid="metric-projected" />
        <Metric label="Web Articles" value={counts.articles ?? 0} sub="published SEO" testid="metric-articles" />
        <Metric label="Content Assets" value={counts.content ?? 0} sub="saved posts" testid="metric-content" />
        <Metric label="Scheduled" value={counts.scheduled ?? 0} sub="in calendar" testid="metric-scheduled" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Gauge card with tracing border */}
        <div className="lg:col-span-1 relative p-[1px] tracing-border">
          <Card className="h-full flex flex-col items-center justify-center bg-card" testid="gauge-card">
            <Overline className="self-start mb-2">CURRENT STANDING</Overline>
            {data?.reputation_score != null ? (
              <>
                <Gauge score={data.reputation_score} />
                <p className="text-xs text-muted-foreground text-center mt-4 px-4">
                  {data.latest_report?.summary?.slice(0, 120)}
                </p>
              </>
            ) : (
              <div className="py-12 text-center">
                <p className="text-sm text-muted-foreground mb-4">No reputation scan yet.</p>
                <button
                  onClick={() => navigate("/reputation")}
                  data-testid="run-first-scan-btn"
                  className="bg-primary text-primary-foreground font-semibold px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors duration-200"
                >
                  Run first scan
                </button>
              </div>
            )}
          </Card>
        </div>

        {/* Sentiment + trend */}
        <div className="lg:col-span-2 grid grid-rows-2 gap-4">
          <Card testid="sentiment-card">
            <Overline>SENTIMENT DISTRIBUTION</Overline>
            {sentiment ? (
              <div className="flex items-center gap-6 mt-2">
                <div style={{ width: 120, height: 120 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={pie} dataKey="value" innerRadius={38} outerRadius={58} paddingAngle={2} stroke="none">
                        {pie.map((e, i) => (
                          <Cell key={i} fill={e.fill} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {pie.map((e) => (
                    <div key={e.name} className="flex items-center gap-2 text-sm">
                      <span className="h-2.5 w-2.5" style={{ background: e.fill }} />
                      <span className="text-muted-foreground w-16">{e.name}</span>
                      <span className="font-mono font-semibold">{e.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <Empty>Run a reputation scan to see sentiment breakdown.</Empty>
              </div>
            )}
          </Card>

          <Card testid="trend-card">
            <Overline>REPUTATION TREND</Overline>
            {data?.trend?.length > 1 ? (
              <div style={{ height: 120 }} className="mt-2">
                <ResponsiveContainer>
                  <AreaChart data={data.trend}>
                    <defs>
                      <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#E9AC0C" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#E9AC0C" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1c1c21" />
                    <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 10 }} />
                    <YAxis domain={[0, 100]} tick={{ fill: "#6b7280", fontSize: 10 }} width={24} />
                    <Tooltip contentStyle={{ background: "#0f0f0f", border: "1px solid #262626", fontSize: 12 }} />
                    <Area type="monotone" dataKey="score" stroke="#E9AC0C" strokeWidth={2.5} fill="url(#trendFill)" dot={{ fill: "#9D3FFF", r: 3 }} activeDot={{ r: 5, fill: "#E9AC0C" }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="mt-4">
                <Empty>Run multiple scans over time to track your reputation trend.</Empty>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Quick actions */}
      <div>
        <Overline className="mb-3">QUICK ACTIONS</Overline>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            [Detective, "New Reputation Scan", "/reputation", "action-scan"],
            [PenNib, "Generate Content", "/content", "action-content"],
            [ImageIcon, "Create Brand Image", "/images", "action-image"],
            [CalendarBlank, "Plan Calendar", "/calendar", "action-calendar"],
          ].map(([Icon, label, to, tid]) => (
            <button
              key={to}
              data-testid={tid}
              onClick={() => navigate(to)}
              className="bg-card border border-border p-5 text-left group hover:border-primary transition-colors duration-200"
            >
              <Icon size={24} weight="bold" className="text-primary mb-3" />
              <div className="text-sm font-medium flex items-center justify-between">
                {label}
                <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
